import React, { createContext, useContext, useState } from "react";

// Create the Context
const OnboardingContext = createContext();

// Provider Component
export const OnboardingProvider = ({ children }) => {
  // This will store your user details (obtained on login/registration)
  const [user, setUser] = useState(null);

  // This object holds data for each onboarding step (e.g., step1, step2, etc.)
  const [onboardingData, setOnboardingData] = useState({});

  /**
   * Call this after login (or when fetching data) to set the user info.
   * The user object can include fields such as `onboarding_current_step` and others.
   */
  const updateUser = (userData) => {
    setUser(userData);
  };

  /**
   * Update the data for a particular onboarding step.
   * @param {string} step - The step identifier (e.g., 'step1', 'step2', etc.)
   * @param {object} data - The data to store for that step.
   */
  const updateOnboardingData = (step, data) => {
    setOnboardingData((prevData) => ({
      ...prevData,
      [step]: data,
    }));
  };

  /**
   * Update the user's current onboarding step.
   * This might be necessary when the user completes a new step.
   * @param {number} step - The current step number the user has reached.
   */
  const updateCurrentStep = (step) => {
    setUser((prevUser) => ({
      ...prevUser,
      onboarding_current_step: step,
    }));
  };

  /**
   * Optional: A function to reset onboarding (e.g., on logout)
   */
  const resetOnboarding = () => {
    setUser(null);
    setOnboardingData({});
  };

  return (
    <OnboardingContext.Provider
      value={{
        user,
        onboardingData,
        updateUser,
        updateOnboardingData,
        updateCurrentStep,
        resetOnboarding,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

// Custom hook for easier context usage in components.
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
